import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import Button from '@/components/ui/Button';
import { StarIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { reviewsApi } from '@/services/api';
import { getPrimaryImage } from '@/utils/imageUtils';
import toast from 'react-hot-toast';

interface Review {
  id: number;
  product_id: number;
  product_name?: string;
  product_image?: string;
  product?: {
    id: number;
    name: string;
    images?: any;
  };
  rating: number;
  title: string;
  comment: string;
  is_verified_purchase: boolean;
  created_at: string;
  is_approved: boolean;
}

const ReviewsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await reviewsApi.getMyReviews();
        // Transform API response to match our interface
        const transformedReviews: Review[] = response.data.map((review: any) => ({
          id: review.id,
          product_id: review.product_id,
          product_name: review.product?.name || 'Unknown Product',
          product_image: review.product ? getPrimaryImage(review.product.images) : '/api/placeholder/100/100',
          rating: review.rating,
          title: review.title || '',
          comment: review.comment || '',
          is_verified_purchase: review.is_verified_purchase || false,
          created_at: review.created_at,
          is_approved: review.is_approved !== undefined ? review.is_approved : true
        }));
        setReviews(transformedReviews);
      } catch (err: any) {
        console.error('Error fetching reviews:', err);
        setError(err.response?.data?.detail || 'Failed to load reviews');
        toast.error('Failed to load reviews');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchReviews();
    }
  }, [user]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`h-5 w-5 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getStatusColor = (isApproved: boolean) => {
    return isApproved
      ? 'bg-green-100 text-green-800'
      : 'bg-yellow-100 text-yellow-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Reviews</h1>
        <p className="mt-2 text-gray-600">Reviews you've written for products</p>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <StarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
          <p className="text-gray-600 mb-6">Start reviewing products you've purchased</p>
          <Button onClick={() => window.location.href = '/buyer/orders'}>
            View My Orders
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <img
                    src={review.product_image}
                    alt={review.product_name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {review.product_name}
                      </h3>
                      {review.is_verified_purchase && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex">
                        {renderStars(review.rating)}
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <h4 className="text-md font-medium text-gray-900 mb-2">
                      {review.title}
                    </h4>
                    
                    <p className="text-gray-600 mb-4">
                      {review.comment}
                    </p>
                    
                    <div className="flex items-center space-x-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(review.is_approved)}`}>
                        {review.is_approved ? 'Approved' : 'Pending Approval'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `/products/${review.product_id}`}
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View Product
                  </Button>
                  {!review.is_approved && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          // Navigate to edit review page or show edit modal
                          // For now, just show a message
                          toast.info('Edit review functionality coming soon');
                        } catch (err) {
                          console.error('Error editing review:', err);
                        }
                      }}
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;

